import type { BccData } from './useBccData'

// ── Cash safety — the single definition (§9.3-4, §9.3-5) ──────────────────────
//
// Two things were wrong before this module existed:
//
//   Financial Position asked  `profit.net >= expenses`.
//   But profit.net is already revenue *minus* those same expenses, so the test
//   really read `revenue - expenses >= expenses` — it demanded a net margin
//   above 50% and therefore said "Warning" forever, for any real retailer.
//
//   Today's Decisions asked   `collected - expenses`.
//   A different question, on the same card set, giving a different answer.
//
// The fix is not a better formula for profit — it is to stop asking a profit
// question at all. "Do I have enough liquidity?" is a cash question, so this
// compares money that has arrived (and money confidently arriving) against money
// that must go out. No profit figure participates.
//
// One deliberate approximation: COGS for this period's orders was paid earlier,
// at purchase time. It is counted as an outflow here because a running store
// restocks continuously, so this period's COGS stands in for this period's
// inventory spend. Stated plainly rather than hidden.

export type CashSafetyStatus = 'safe' | 'tight' | 'at_risk'

export type CashSafety = {
  status: CashSafetyStatus

  // Inflow
  arrived:  number   // COD settled into the account this period
  expected: number   // delivered, awaiting remittance — all ages

  // Outflow
  variableCosts: number   // scale with order volume
  fixedCosts:    number   // rent, payroll, subscriptions
  totalOut:      number

  // Result
  surplus:          number   // arrived - totalOut (negative = gap)
  surplusWithExpected: number
  coveragePct:      number   // arrived ÷ totalOut, 0–200 clamp
  coverageWithExpectedPct: number

  // Caveats the reader needs in order to trust the number
  hasFixedCosts: boolean
}

export function computeCashSafety(data: BccData): CashSafety {
  const { dashboard, cash } = data

  const variableCosts =
    dashboard.cogs.total +
    dashboard.shipping.used +
    dashboard.cod.carrierFees +
    dashboard.gateway.fees +
    dashboard.marketing.spend +
    dashboard.returns.handlingCost

  const fixedCosts = dashboard.fixedExpenses.total
  const totalOut   = variableCosts + fixedCosts

  const arrived  = cash.collectedThisMonth
  // All-time, deliberately: money owed to the merchant is owed regardless of when
  // the order was placed (see computeCashPosition). Both channels count — COD
  // waiting on the carrier, and online orders waiting on the gateway payout.
  const expected = cash.totalCodAtRisk + cash.gatewayPendingAmount

  const surplus             = arrived - totalOut
  const surplusWithExpected = arrived + expected - totalOut

  const pct = (n: number) => (totalOut > 0 ? Math.min((n / totalOut) * 100, 200) : 100)

  // Three states, because "covered only once the carrier pays out" is a real and
  // common position that neither "safe" nor "at risk" describes honestly.
  const status: CashSafetyStatus =
    surplus >= 0             ? 'safe'  :
    surplusWithExpected >= 0 ? 'tight' : 'at_risk'

  return {
    status,
    arrived,
    expected,
    variableCosts,
    fixedCosts,
    totalOut,
    surplus,
    surplusWithExpected,
    coveragePct:             pct(arrived),
    coverageWithExpectedPct: pct(arrived + expected),
    hasFixedCosts:           dashboard.fixedExpenses.itemCount > 0,
  }
}

export const STATUS_LABEL: Record<CashSafetyStatus, string> = {
  safe:    'Safe',
  tight:   'Tight',
  at_risk: 'At risk',
}

export const STATUS_TONE: Record<CashSafetyStatus, 'success' | 'warning' | 'critical'> = {
  safe:    'success',
  tight:   'warning',
  at_risk: 'critical',
}
